<template>
  <div class="spline-property">
    <n-collapse :default-expanded-names="defaultExpanded" class="pm-collapse">
      <n-collapse-item v-if="showAdvancedSections" title="现有几何关系" name="relations">
        <SelectionListFrame
          :items="existingRelations"
          selection-behavior="ctrl-multiple"
          list-role="list"
          :visible-rows="4"
          context-menu-enabled
          :is-item-delete-enabled="isSketchRelationSnapshotDeleteEnabled"
          :show-delete-all-action="existingRelations.some(isSketchRelationSnapshotDeleteEnabled)"
          item-key-field="relationId"
          @delete-item="handleDeleteRelationSelection"
          @delete-all="handleDeleteAllRelations"
        >
          <template #item="{ item }">
            <SketchRelationDiagnosticText
              :item="item"
              :label="resolveRelationLabel(item)"
            />
          </template>
        </SelectionListFrame>
      </n-collapse-item>

      <n-collapse-item v-if="showAdvancedSections" title="添加几何关系" name="add-relations">
        <n-space size="small">
          <n-button size="small" quaternary>固定</n-button>
        </n-space>
      </n-collapse-item>

      <n-collapse-item v-if="showAdvancedSections" title="选项" name="options">
        <div class="option-list">
          <n-checkbox
            :checked="constructionChecked"
            :disabled="!canEditOptions"
            @update:checked="handleConstructionToggle"
          >
            作为构造线
          </n-checkbox>
          <n-checkbox
            :checked="options.showCurvature"
            :disabled="true"
            @update:checked="handleShowCurvature"
          >
            显示曲率
          </n-checkbox>
          <n-checkbox
            :checked="options.keepInternalContinuity"
            :disabled="!canEditOptions"
            @update:checked="handleKeepInternalContinuity"
          >
            保持内部连续性
          </n-checkbox>
        </div>
      </n-collapse-item>

      <n-collapse-item title="参数" name="parameters">
        <div class="param-grid">
          <div class="param-row">
            <n-text depth="3" class="param-label">点</n-text>
            <n-input-number
              :value="pointIndexValue"
              size="small"
              :disabled="!canEditParameters"
              :show-button="true"
              :precision="0"
              :step="1"
              :min="1"
              :max="maxPointIndex"
              @update:value="handlePointIndexChange"
            />
          </div>
          <div class="param-row">
            <n-text depth="3" class="param-label">X</n-text>
            <n-input-number
              :value="pointX"
              size="small"
              :disabled="!canEditParameters"
              :show-button="true"
              :precision="8"
              :step="1"
              @update:value="(value) => handlePointAxisChange('x', value)"
            />
          </div>
          <div class="param-row">
            <n-text depth="3" class="param-label">Y</n-text>
            <n-input-number
              :value="pointY"
              size="small"
              :disabled="!canEditParameters"
              :show-button="true"
              :precision="8"
              :step="1"
              @update:value="(value) => handlePointAxisChange('y', value)"
            />
          </div>
        </div>
      </n-collapse-item>
    </n-collapse>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onBeforeUnmount } from 'vue';
import {
  NButton,
  NCheckbox,
  NCollapse,
  NCollapseItem,
  NInputNumber,
  NSpace,
  NText,
} from 'naive-ui';
import type { PropertyManagerContext, SplineOptions, SplineParameterUpdate } from '@/core';
import * as THREE from 'three';
import { useViewer } from '@/hooks';
import { useGetSelectSpline } from '@/hooks';
import SelectionListFrame from '@/components/editor/SelectionListFrame.vue';
import SketchRelationDiagnosticText from '../common/SketchRelationDiagnosticText.vue';
import {isSketchRelationSnapshotDeleteEnabled} from '@/utils/sketch/isSketchRelationSnapshotDeleteEnabled';
import {
  isSketchSplineObject,
  roundNullableToEightDecimalPlaces,
  type SketchRelationSnapshot,
  //------ 统一单位转换 开始------
  convertMmToM,
  convertMToMm,
  roundToEightDecimalPlaces,
  //------ 统一单位转换 结束------
} from '@puppycad/cad-core';

defineOptions({
  inheritAttrs: false,
});

interface SplinePropertySectionProps {
  /** 属性面板上下文，用于统一提交草图属性编辑。 */
  context?: PropertyManagerContext;
}

const props = defineProps<SplinePropertySectionProps>();

const defaultOptions: SplineOptions = {
  forConstruction: false,
  showCurvature: false,
  keepInternalContinuity: false,
};

const { getViewer } = useViewer();
const { getSelected: getSelectedSpline, onChanged: onSplineChanged } = useGetSelectSpline();

const localSplineOptions = ref<SplineOptions>({ ...defaultOptions });

/** 是否有样条曲线被选中。 */
const isSplineSelected = ref(false);
/** “作为构造线”勾选状态镜像，避免直接依赖非响应式草图对象字段。 */
const constructionCheckedValue = ref(false);
/** 当前选中样条曲线的现有关系快照列表。 */
const existingRelations = ref<SketchRelationSnapshot[]>([]);
/** 当前激活的控制点序号。 */
const activePointIndex = ref(1);
/** 属性编辑刷新版本，用于让非响应式 Three.js 对象变更后刷新面板计算值。 */
const propertyEditRevision = ref(0);

//------ 样条构造线勾选状态同步 开始------
/**
 * 同步“作为构造线”勾选状态。
 *
 * 设计说明：
 * 选中的样条曲线是 cad-core 运行时对象，不能交给 Vue 做响应式代理。
 * 因此属性面板只保存 boolean 镜像，并在选择变化或用户操作后显式同步。
 *
 * @param spline 当前选中的样条曲线对象。
 */
function syncConstructionCheckedState(spline: ReturnType<typeof getSelectedSpline>): void {
  if (spline) {
    constructionCheckedValue.value = Boolean(spline.isConstruction);
    return;
  }
  constructionCheckedValue.value = Boolean(localSplineOptions.value.forConstruction);
}
//------ 样条构造线勾选状态同步 结束------

//------ 样条选中订阅顺序修复 开始------
const splineChangedSub = onSplineChanged().subscribe((spline) => {
  isSplineSelected.value = spline !== null;
  refreshExistingRelations(spline);
  // 选中变化时同步参数
  const renderer = getViewer();
  localSplineOptions.value = renderer?.getSplineDrawingOptions?.() ?? { ...defaultOptions };
  syncConstructionCheckedState(spline);
  // 重置控制点索引
  activePointIndex.value = 1;
});
onBeforeUnmount(() => {
  splineChangedSub.unsubscribe();
});
//------ 样条选中订阅顺序修复 结束------
const showAdvancedSections = computed(() => isSplineSelected.value);
const canEditOptions = computed(() => isSplineSelected.value && Boolean(getViewer()));
const canEditParameters = computed(() => isSplineSelected.value && Boolean(getViewer()));
const options = computed(() => localSplineOptions.value);

const constructionChecked = computed(() => constructionCheckedValue.value);

const controlPoints2d = computed(() => {
  void propertyEditRevision.value;
  if (!isSplineSelected.value) {
    return [] as Array<{ x: number; y: number }>;
  }
  const selectedObject = getSelectedSpline();
  if (!selectedObject || !isSketchSplineObject(selectedObject)) {
    return [] as Array<{ x: number; y: number }>;
  }
  return selectedObject.getControlPoints().map((point) => {
    const local = selectedObject.referencePlane.worldToLocal(point.getPosition());
    return {
      x: local.x,
      y: local.y,
    };
  });
});

const pointCount = computed(() => controlPoints2d.value.length);
const maxPointIndex = computed(() => (pointCount.value > 0 ? pointCount.value : 1));

watch(pointCount, (count) => {
  if (count <= 0) {
    activePointIndex.value = 1;
    return;
  }
  const next = Math.min(Math.max(1, activePointIndex.value), count);
  if (next !== activePointIndex.value) {
    activePointIndex.value = next;
  }
});

const activePoint = computed(() => {
  const index = activePointIndex.value - 1;
  if (index < 0 || index >= controlPoints2d.value.length) {
    return null;
  }
  return controlPoints2d.value[index];
});

const pointIndexValue = computed(() => activePointIndex.value);
//------ 统一单位转换 开始------
const pointX = computed(() => {
  const x = activePoint.value?.x ?? null;
  if (x === null) return null;
  return roundToEightDecimalPlaces(convertMToMm(x));
});
const pointY = computed(() => {
  const y = activePoint.value?.y ?? null;
  if (y === null) return null;
  return roundToEightDecimalPlaces(convertMToMm(y));
});
//------ 统一单位转换 结束------

const defaultExpanded = computed(() => {
  if (showAdvancedSections.value) {
    return ['relations', 'add-relations', 'options', 'parameters'];
  }
  return ['parameters'];
});

function updateOptions(patch: Partial<SplineOptions>): void {
  const renderer = getViewer();
  const currentOptions = renderer?.getSplineDrawingOptions?.();
  if (!renderer || !currentOptions) {
    return;
  }
  // 当前样条选项尚无独立 setter，先按现有模式能力收口到专用入口。
  if (typeof patch.mode === 'string') {
    renderer.setStyleSplineDrawingMode?.(patch.mode);
  }
  localSplineOptions.value = { ...localSplineOptions.value, ...patch };
  syncConstructionCheckedState(getSelectedSpline());
}

/**
 * 将样条属性编辑统一交给 PropertyManagerContext 收口。
 * @param spline 被编辑的样条对象。
 */
function finalizeSplinePropertyEdit(spline: ReturnType<typeof getSelectedSpline>): void {
  if (!spline) {
    return;
  }
  props.context?.finalizeSketchPropertyEdit?.(spline, {
    reason: 'property-edit',
    sourceObjectId: spline.uuid,
  });
  propertyEditRevision.value += 1;
}

function worldToLocal2D(
  point: THREE.Vector3,
  origin: THREE.Vector3,
  xAxis: THREE.Vector3,
  yAxis: THREE.Vector3
): THREE.Vector2 {
  const relative = point.clone().sub(origin);
  return new THREE.Vector2(relative.dot(xAxis), relative.dot(yAxis));
}

function localToWorld2D(
  local: THREE.Vector2,
  origin: THREE.Vector3,
  xAxis: THREE.Vector3,
  yAxis: THREE.Vector3
): THREE.Vector3 {
  return origin.clone().addScaledVector(xAxis, local.x).addScaledVector(yAxis, local.y);
}

function updateSelectedSpline(update: SplineParameterUpdate): void {
  const selectedSpline = getSelectedSpline();
  if (!selectedSpline || !isSketchSplineObject(selectedSpline)) {
    return;
  }
  const controlPoints = selectedSpline.getControlPoints();
  const index = Math.min(Math.max(0, Math.floor(update.index)), controlPoints.length - 1);
  const targetPoint = controlPoints[index];
  if (!targetPoint) {
    return;
  }
  targetPoint.setCoordinateOnReferencePlane(update.axis, update.value);
  finalizeSplinePropertyEdit(selectedSpline);
}

//------ 样条曲线现有几何关系 开始------

/**
 * 刷新当前选中样条曲线的现有关系列表。
 * @param spline 选中的样条曲线对象（null 表示无选中）。
 */
function refreshExistingRelations(spline: ReturnType<typeof getSelectedSpline>): void {
  if (!spline) {
    existingRelations.value = [];
    return;
  }
  existingRelations.value = spline.constraints.getRelationSnapshots();
}

/**
 * 将关系快照转为显示标签。
 * @param item 列表项。
 * @returns 显示标签。
 */
function resolveRelationLabel(item: unknown): string {
  const snapshot = item as SketchRelationSnapshot | null;
  if (!snapshot) {
    return '';
  }
  return snapshot.baseDisplayName || snapshot.name || String(snapshot.relationType);
}

async function removeSplineRelation(
  spline: NonNullable<ReturnType<typeof getSelectedSpline>>,
  relationId: number,
): Promise<boolean> {
  return await props.context?.removeSketchRelation?.(spline, relationId) ?? false;
}

/**
 * 处理右键删除单个关系。
 * @param payload SelectionListFrame delete-item 回调参数。
 */
async function handleDeleteRelationSelection(payload: { item: unknown; index: number }): Promise<void> {
  const spline = getSelectedSpline();
  if (!spline) {
    return;
  }
  const snapshot = payload.item as SketchRelationSnapshot;
  if (!snapshot || !Number.isFinite(snapshot.id) || !isSketchRelationSnapshotDeleteEnabled(snapshot)) {
    return;
  }
  if (!await removeSplineRelation(spline, snapshot.id)) {
    return;
  }
  const viewer = getViewer();
  viewer?.removeObjectConstraintMarker?.(spline, snapshot.id);
  refreshExistingRelations(spline);
}

/**
 * 处理右键删除全部关系。
 */
async function handleDeleteAllRelations(): Promise<void> {
  const spline = getSelectedSpline();
  if (!spline) {
    return;
  }
  const viewer = getViewer();
  for (const snapshot of existingRelations.value.filter(isSketchRelationSnapshotDeleteEnabled)) {
    if (!await removeSplineRelation(spline, snapshot.id)) {
      return;
    }
    viewer?.removeObjectConstraintMarker?.(spline, snapshot.id);
  }
  refreshExistingRelations(spline);
}

//------ 样条曲线现有几何关系 结束------

function handleConstructionToggle(value: boolean): void {
  if (isSplineSelected.value) {
    const selectedSpline = getSelectedSpline();
    if (selectedSpline) {
      // 直接通过对象 setter 修改构造线状态（setter 内部切换材质）。
      selectedSpline.isConstruction = value;
      const viewer = getViewer();
      viewer?.syncObjectBaseVisualState?.(selectedSpline);
      if (value || !viewer?.shadedSketchContoursEnabled) {
        selectedSpline.clearContourDisplay?.();
      } else if (selectedSpline.canCreateContour?.()) {
        // 开链样条不能创建轮廓，恢复构造线状态时必须保持静默。
        selectedSpline.syncContour?.();
      }
      constructionCheckedValue.value = value;
      finalizeSplinePropertyEdit(selectedSpline);
      syncConstructionCheckedState(selectedSpline);
      return;
    }
  }
  updateOptions({ forConstruction: value });
  constructionCheckedValue.value = value;
}

function handleShowCurvature(value: boolean): void {
  updateOptions({ showCurvature: value });
}

function handleKeepInternalContinuity(value: boolean): void {
  updateOptions({ keepInternalContinuity: value });
}

function handlePointIndexChange(value: number | null): void {
  if (value === null || Number.isNaN(value)) {
    return;
  }
  const count = maxPointIndex.value;
  const next = Math.min(Math.max(1, Math.round(value)), count);
  activePointIndex.value = next;
}

function handlePointAxisChange(axis: 'x' | 'y', value: number | null): void {
  if (!canEditParameters.value || value === null || Number.isNaN(value)) {
    return;
  }
  const index = activePointIndex.value - 1;
  //------ 统一单位转换 开始------
  // 用户输入的毫米值转换为米传给底层
  const update: SplineParameterUpdate = { type: 'point', index, axis, value: convertMmToM(value) };
  //------ 统一单位转换 结束------
  updateSelectedSpline(update);
}
</script>

<style scoped>
.spline-property {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.option-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.param-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.param-row {
  display: grid;
  grid-template-columns: 40px 1fr;
  gap: 8px;
  align-items: center;
}

.param-label {
  font-size: 12px;
}
</style>
